"use client";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import * as Sentry from "@sentry/browser";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "./ui/form";
import { toast } from "sonner";
import { useSession } from "@/lib/auth-client";
type FeedbackDialogProps = {
  children: React.ReactElement<React.ButtonHTMLAttributes<HTMLButtonElement>>;
};
export function FeedbackLink({ children }: { children: React.ReactNode }) {
  return (
    <FeedbackDialog>
      <button className="underline decoration-current/30 underline-offset-2 hover:decoration-current">
        {children}
      </button>
    </FeedbackDialog>
  );
}
const feedbackSchema = z.object({
  message: z.string().min(1, "Feedback is required"),
  email: z.string().email("Invalid email address").optional().or(z.literal("")),
});
type FeedbackFormData = z.infer<typeof feedbackSchema>;
export function FeedbackDialog({ children }: FeedbackDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const session = useSession();
  const isLoggedIn = !!session.data;
  const form = useForm<FeedbackFormData>({
    resolver: zodResolver(feedbackSchema),
    defaultValues: {
      message: "",
      email: "",
    },
  });
  const onSubmit = async (data: FeedbackFormData) => {
    try {
      // Send user feedback to Sentry
      Sentry.captureFeedback(
        {
          message: data.message,
          email: data.email || undefined,
        },
        {
          includeReplay: true,
          // TODO: Allow users to add a screenshot
          attachments: [],
        },
      );
      form.reset();
      setIsOpen(false);
      toast.success("Thanks for your feedback!");
    } catch (error) {
      Sentry.captureException(error);
      toast.error("Failed to submit feedback");
    }
  };
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
  };
  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger render={children}></DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Give feedback</DialogTitle>
          <DialogDescription>Let me know what you think!</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {!isLoggedIn && (
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email (optional)</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="you@example.com"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Your feedback</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Describe your experience, suggestions for improvement, or any issues you've encountered"
                      className="min-h-[120px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="submit">Submit</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
